import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './AuditLogPanel.css';

/**
 * Renders an entity's audit trail (who changed what, when).
 * Mirrors the Audit Log tab on AssetDetail, but fetches its own data so it
 * can be dropped into any tabbed detail page.
 *
 * @param {string} fetchUrl - e.g. `/api/v1/customers/${id}/audit-log`
 * @param {Object} fieldLabels - field_name -> display label map
 * @param {string} emptyMessage - shown when no entries exist
 */
function AuditLogPanel({ fetchUrl, fieldLabels, emptyMessage }) {
  const { authFetch } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(fetchUrl);
        if (!response.ok) throw new Error('Failed to load audit log');
        const data = await response.json();
        if (!cancelled) setEntries(data.data || []);
      } catch (err) {
        console.error('Failed to fetch audit log:', err);
        if (!cancelled) setError('Failed to load change history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [fetchUrl, authFetch]);

  if (loading) {
    return <div className="audit-log-section"><div className="empty-state">Loading change history…</div></div>;
  }

  if (error) {
    return <div className="audit-log-section"><div className="empty-state">{error}</div></div>;
  }

  const groupedByTimestamp = entries.reduce((acc, entry) => {
    const key = `${entry.changed_at}-${entry.changed_by_name}-${entry.change_type}`;
    if (!acc[key]) {
      acc[key] = {
        changed_at: entry.changed_at,
        changed_by_name: entry.changed_by_name,
        change_type: entry.change_type,
        changes: []
      };
    }
    acc[key].changes.push(entry);
    return acc;
  }, {});

  return (
    <div className="audit-log-section">
      <h3>Change History</h3>
      {entries.length === 0 ? (
        <div className="empty-state">{emptyMessage || 'No changes have been recorded yet.'}</div>
      ) : (
        <div className="audit-timeline">
          {Object.values(groupedByTimestamp).map((group, idx) => (
            <div key={idx} className="audit-entry">
              <div className="audit-header">
                <span className="audit-user">{group.changed_by_name}</span>
                <span className={`audit-type ${group.change_type}`}>
                  {group.change_type === 'stage_change' ? 'stage change' : group.change_type}
                </span>
                <span className="audit-date">
                  {new Date(group.changed_at).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <div className="audit-changes">
                {group.changes.map((change, cIdx) => (
                  change.field_name ? (
                    <div key={cIdx} className="audit-change-row">
                      <span className="audit-field">{(fieldLabels && fieldLabels[change.field_name]) || change.field_name}</span>
                      <span className="audit-old">{change.old_value || '(empty)'}</span>
                      <span className="audit-arrow">→</span>
                      <span className="audit-new">{change.new_value || '(empty)'}</span>
                    </div>
                  ) : (
                    <div key={cIdx} className="audit-change-row">
                      <span className="audit-summary">{change.change_summary}</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AuditLogPanel;
